import React, { useEffect } from 'react';
import { get } from 'lodash';
import { FormFieldDependentSelect as IFormFieldDependentSelect } from '@publishing-engine/domain/types/article';
import { Flex, Text } from '@chakra-ui/react';
import { RadioGroup } from '../RadioGroup.js';
import { useFormContext } from 'react-hook-form';
import { TextArea } from '@morressier/alembic';
import { FieldLabel } from 'src/components/Field';
import FormFieldErrorMessage from './common/FormFieldErrorMessage';
import { parseStringWithLinks } from 'src/utils/parseStringWithLinks.js';

interface IFormFieldDependentSelectProps {
  config: IFormFieldDependentSelect;
  isEditingAllowed?: boolean;
}

const FormFieldDependentSelect: React.FC<IFormFieldDependentSelectProps> = ({
  config,
  isEditingAllowed = true
}) => {
  const {
    formState: { errors },
    register,
    watch,
    control,
    setValue
  } = useFormContext();
  const {
    name,
    params: { labels, validations },
    caseDataFields: { main: field, dependsOn }
  } = config;
  const dependsOnField = `${field}.${dependsOn}` as const;
  const mainField = `${field}.value` as const;

  const optionKeys = Object.keys(labels.options);

  const options = optionKeys.map(key => {
    return {
      value: key === 'true' ? true : false,
      children: (
        <Flex flexDirection="column">
          <Text textStyle="textBody">{labels.options[key]?.main}</Text>
          <Text textStyle="textBody">{labels.options[key]?.secondary}</Text>
        </Flex>
      )
    };
  });

  const depReq = validations.dependentRequired;
  const yesRequired =
    (typeof depReq === 'object' ? depReq.yes : depReq) ?? validations.required;
  const noRequired =
    (typeof depReq === 'object' ? depReq.no : depReq) ?? validations.required;

  const error = get(errors, field);

  const exists = watch(dependsOnField);
  const fieldValue = watch(mainField);

  const hasYesSection = Boolean(labels.section);
  const hasNoSection = Boolean(labels.sectionNo);
  useEffect(() => {
    if (
      (exists === true && !hasYesSection) ||
      (exists === false && !hasNoSection)
    ) {
      setValue(mainField, '');
    }
  }, [exists, hasYesSection, hasNoSection, mainField, setValue]);

  return (
    <Flex flexDirection="column" gap={6}>
      <FieldLabel
        isRequired={validations.required}
        label={labels.main}
        subText={labels.subtitle}
        helpText={labels.helpText}
      />
      <Flex flexDirection="column">
        <RadioGroup
          name={dependsOnField}
          control={control}
          options={options}
          required={validations.required}
          gap={2}
          isEditingAllowed={isEditingAllowed}
        />
      </Flex>
      {exists === true &&
      labels.section &&
      (labels.section.title.length > 0 ||
        labels.section.explanation.length > 0) ? (
        <Flex flexDirection="column" gap={2}>
          <FieldLabel
            label={labels.section.title}
            subText={parseStringWithLinks(labels.section.explanation)}
            isRequired={yesRequired}
            helpText={labels.helpText}
          />

          <TextArea
            value={fieldValue}
            id={name}
            {...register(mainField, {
              required: yesRequired,
              maxLength: validations.maxLength
            })}
            isInvalid={!!error}
            isReadOnly={!isEditingAllowed}
          />
        </Flex>
      ) : exists === false && labels.sectionNo ? (
        <Flex flexDirection="column" gap={2}>
          <FieldLabel
            label={labels.sectionNo.title}
            subText={parseStringWithLinks(labels.sectionNo?.explanation)}
            isRequired={noRequired}
            helpText={labels.helpText}
          />

          <TextArea
            value={fieldValue}
            id={name}
            {...register(mainField, {
              required: noRequired,
              maxLength: validations.maxLength
            })}
            isInvalid={!!error}
            isReadOnly={!isEditingAllowed}
          />
        </Flex>
      ) : null}
      <FormFieldErrorMessage
        config={config}
        label={
          exists === true && labels.section
            ? labels.section.title
            : exists === false && labels.sectionNo
            ? labels.sectionNo.title
            : labels.main
        }
      />
    </Flex>
  );
};

export default FormFieldDependentSelect;
